import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import { Box, Button, CircularProgress, Grid, Menu, MenuItem, Typography } from "@mui/material"
import React, { useCallback, useContext, useMemo, useRef, useState } from "react"
import { DashboardButton } from "../../../../../../../../components/charts/components/DashboardButton"
import { ErrorContext } from "../../../../../../../../components/layout/error-snackbar"
import { BSModelFileContextProvider } from "../../../../../../../../core/context/beem-shot/BSBimModel/BSBimModelFileContext"
import { BSBuildingResultContext } from "../../../../../../../../core/context/beem-shot/BSBuildingResult/BSBuildingResultContext"
import { BSVariantContext } from "../../../../../../../../core/context/beem-shot/BSVariant/BSVariantContext"
import { ProjectContext } from "../../../../../../../../core/context/project/ProjectContext"
import { BSMaterialResultDashboard } from "../../../../../../../../core/dto/beem-shot/BSMaterialResult/BSMaterialResultDashboard"
import { toCsvString } from "../../../../../../../../core/services/csv-service"
import { downloadPDF, saveAsFile } from "../../../../../../../../core/services/helper-service"
import { useDownloadBSMaterialPleaide } from "../../../../../../../../core/services/pleaide-service"
import { BSMorphologyContext } from "../BSBuildingMorphologyTab/context/BSMorphologyContext"
import { BSBuildingDashboardContext, BSBuildingDashboardContextProvider } from "./BSBuildingDashboardContext"
import { BSBiosourceTab } from "./components/BSBiosourceTab/BSBiosourceTab"
import { BSDashboardTabEnum } from "./components/BSDashboardTabEnum"
import { BSIcConstructionTab } from "./components/BSIcConstructionTab/BSIcConstructionTab"

export function BSBuildingDashboard(): React.JSX.Element {
  return (
    <BSBuildingDashboardContextProvider>
      <BSModelFileContextProvider>
        <BSBuildingDashboardCore />
      </BSModelFileContextProvider>
    </BSBuildingDashboardContextProvider>
  )
}

function BSBuildingDashboardCore(): React.JSX.Element {
  const openErrorSnackbar = useContext(ErrorContext)
  const { project } = useContext(ProjectContext)
  const { selectedVariant } = useContext(BSVariantContext)
  const { bsBuildingResult } = useContext(BSBuildingResultContext)
  const { isEstimating } = useContext(BSMorphologyContext)
  const { allBSMaterialResult } = useContext(BSBuildingDashboardContext)

  const dashboardIcConstructionRef = useRef<HTMLDivElement | null>(null)
  const dashboardBiosourceRef = useRef<HTMLDivElement | null>(null)
  const [tab, setTab] = useState<BSDashboardTabEnum>(BSDashboardTabEnum.ICCONSTRUCTION)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false)
  const [isDownloadingCsv, setIsDownloadingCsv] = useState<boolean>(false)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = useMemo(() => Boolean(anchorEl), [anchorEl])

  const { handleDownloadBSMaterialPleaide } = useDownloadBSMaterialPleaide(
    allBSMaterialResult,
    openErrorSnackbar,
    setIsDownloadingCsv,
    project,
    selectedVariant
  )

  const handleDownloadPdf: () => void = useCallback(() => {
    const pdfName = `${project?.name}-${selectedVariant?.name}`
    if (tab === BSDashboardTabEnum.ICCONSTRUCTION && dashboardIcConstructionRef.current) {
      setIsDownloadingPdf(true)
      downloadPDF(dashboardIcConstructionRef.current, pdfName).finally(() => {
        setIsDownloadingPdf(false)
      })
    } else if (tab === BSDashboardTabEnum.BIOSOURCE && dashboardBiosourceRef.current) {
      setIsDownloadingPdf(true)
      downloadPDF(dashboardBiosourceRef.current, pdfName).finally(() => {
        setIsDownloadingPdf(false)
      })
    } else {
      openErrorSnackbar(new Error("Erreur: impossible de récupérer le tableau de bord"))
    }
  }, [project?.name, selectedVariant?.name, openErrorSnackbar, tab])

  const handleDownloadFile: (fileName: string, columnNames: string[], columns: (keyof BSMaterialResultDashboard)[]) => void =
    useCallback(
      (fileName, columnNames, columns) => {
        if (allBSMaterialResult) {
          setIsDownloadingCsv(true)
          const csvContent = toCsvString(allBSMaterialResult, columns, columnNames)
          const blob = new Blob([`\uFEFF${csvContent}`], { type: "text/csv;charset=utf-8;" }) // BOM ajouté pour Excel
          saveAsFile(blob, fileName)
          setIsDownloadingCsv(false)
        } else {
          openErrorSnackbar(new Error("Les données ne sont pas prêtes à être téléchargées"))
        }
      },
      [allBSMaterialResult, openErrorSnackbar]
    )

  const handleDownloadCsv: () => void = useCallback(() => {
    const columns: (keyof BSMaterialResultDashboard)[] = [
      "bsItemLabel",
      "codeOccurrence",
      "bsMacroComponentLabel",
      "lotLabel",
      "sousLotLabel",
      "nomProduit",
      "ficheId",
      "declarationType",
      "quantity",
      "ficheUniteLabel",
      "re2020CarbonImpactPerSurface",
    ]

    const columnNames: string[] = [
      "Item",
      "Provenance",
      "Macro composant",
      "Lot",
      "Sous lot",
      "Nom du produit",
      "Fiche Id",
      "Type de déclaration",
      "Quantité",
      "Unité",
      "Impact carbone (/m²Sref)",
    ]

    const fileName = `${project?.name}-${selectedVariant?.name}.csv`
    handleDownloadFile(fileName, columnNames, columns)
  }, [project?.name, selectedVariant?.name, handleDownloadFile])

  const switchToICConstruction: () => void = useCallback(() => {
    setTab(BSDashboardTabEnum.ICCONSTRUCTION)
  }, [])

  const switchToBiosource: () => void = useCallback(() => {
    setTab(BSDashboardTabEnum.BIOSOURCE)
  }, [])

  function handleClick(event: React.MouseEvent<HTMLButtonElement>): void {
    setAnchorEl(event.currentTarget)
  }

  function handleClose(): void {
    setAnchorEl(null)
  }

  return (
    <Box
      sx={{
        height: "100%",
        px: 1,
        overflow: "auto",
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          alignContent: "center",
        }}>
        {project && bsBuildingResult && allBSMaterialResult && !isEstimating ? (
          <>
            <Grid container columnSpacing={4} rowGap={2} sx={{ pt: 2 }}>
              {/* Top button */}
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <DashboardButton
                    label="IcConstruction"
                    onClick={switchToICConstruction}
                    variant={tab === BSDashboardTabEnum.ICCONSTRUCTION ? "contained" : "outlined"}
                  />
                  <DashboardButton
                    label="Biosourcé"
                    onClick={switchToBiosource}
                    variant={tab === BSDashboardTabEnum.BIOSOURCE ? "contained" : "outlined"}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button variant="outlined" color="secondary" onClick={handleClick} endIcon={<ArrowDropDownIcon />}>
                    Télécharger les résultats
                  </Button>
                  <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem onClick={handleDownloadPdf}>Export PDF</MenuItem>
                    <MenuItem onClick={handleDownloadCsv}>Export Excel</MenuItem>
                    <MenuItem onClick={handleDownloadBSMaterialPleaide}>Export Pleiades</MenuItem>
                  </Menu>
                </Box>
              </Grid>
            </Grid>

            {tab === BSDashboardTabEnum.ICCONSTRUCTION ? (
              <BSIcConstructionTab
                bsBuildingResult={bsBuildingResult}
                allBSMaterialResult={allBSMaterialResult}
                dashboardRef={dashboardIcConstructionRef}
              />
            ) : (
              <BSBiosourceTab
                bsBuildingResult={bsBuildingResult}
                allBSMaterialResult={allBSMaterialResult}
                dashboardRef={dashboardBiosourceRef}
              />
            )}
            <Box sx={{ height: "1000px" }} />
          </>
        ) : (
          <Box
            sx={{
              height: "70vh",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignContent: "center",
              alignItems: "center",
              gap: 1,
            }}>
            <CircularProgress />
            {isEstimating && <Typography>Rafraîchissement de l'estimation en cours...</Typography>}
          </Box>
        )}
      </Box>
    </Box>
  )
}
